import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { gql } from 'graphql-request'
import { useForm, Controller } from 'react-hook-form'
import { truncate } from 'lodash'
import { BuildingLibraryIcon, GlobeAltIcon, LockClosedIcon } from '@heroicons/react/24/outline'

import { useQuery, useMutation } from '@hooks/graphql'
import { formatShortTimestamp } from '@helpers/format'
import Spinner from '@components/Spinner'
import Pagination from '@components/Pagination'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@components/ui/table'
import { useSubscription } from '../../hooks/subscription'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import Breadcrumbs from '@components/Breadcrumbs'
import { getCourseVisualStyle } from '@helpers/courseUtils'

import NewPathwayButton from './NewPathwayButton'

const CREATE_SERIES_MUTATION = gql`
  mutation createSeries($input: CreateSeriesInput!) {
    createSeries(input: $input) {
      series {
        id
        name
        description
        createdAt
      }
    }
  }
`

const SERIES_QUERY = gql`
  query mySeries($page: Int) {
    mySeries(page: $page, perPage: 10) {
      nodes {
        id
        name
        createdAt
        description
        visibility
        coverImage {
          url
        }
        profile {
          id
          fullName
        }
        organization {
          id
          name
        }
        educatorProjects {
          id
          name
        }
      }
      pagesCount
      nodesCount
    }
  }
`

const VisiblilityBadge = ({ visibility }) => (
  <div className='flex flex-row items-center gap-1 w-fit px-2 py-1 rounded-3xl bg-muted leading-none'>
    <Choose>
      <When condition={visibility === 'PUBLIC'}>
        <GlobeAltIcon className='w-4 h-4' />
        Public
      </When>

      <When condition={visibility === 'PRIVATE'}>
        <LockClosedIcon className='w-4 h-4 ' />
        Private
      </When>

      <When condition={visibility === 'ORGANIZATION'}>
        <BuildingLibraryIcon className='w-4 h-4 ' />
        Organization
      </When>
    </Choose>
  </div>
)

const SeriesList = () => {
  const navigate = useNavigate()
  const { hasProFeatures } = useSubscription()
  const [page, setPage] = useState(1)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { register, handleSubmit, formState: { errors }, control } = useForm({ mode: 'onTouched', defaultValues: { visibility: 'PRIVATE' } })

  const { isLoading, data: { mySeries: { nodes: series = [], nodesCount, pagesCount } = {} } = {} } = useQuery({
    queryKey: ['myLibrarySeries', page],
    gqlQuery: SERIES_QUERY,
    variables: { page },
    keepPreviousData: true
  })
  const { mutate: createSeries, isLoading: isCreating } = useMutation({
    gqlMutation: CREATE_SERIES_MUTATION,
    onSuccess: data => navigate(`/educators/courses/${data.createSeries.series.id}`)
  })

  const onSubmit = data => createSeries({ input: data })

  return (
    <>
      <Breadcrumbs>
        <Breadcrumbs.Link label='Pathways' to={`/educators/courses`} />
      </Breadcrumbs>

      <Choose>
        <When condition={isLoading}>
          <Spinner className='relative top-0 left-0 flex items-center justify-center w-full h-screen' />
        </When>

        <Otherwise>
          <div className='flex flex-col md:px-5 mt-3 px-5'>
            {/* Container for Title and Button */}
            <div className="flex justify-between items-center mb-3">
              <h1 className="text-2xl font-extrabold">Pathways</h1>

              {/* Group Button and Tooltip */}
              <div className="flex items-center">
                <NewPathwayButton
                  hasProFeatures={hasProFeatures}
                  showModal={() => setIsModalOpen(true)}
                />
              </div>
            </div>

            <div className='overflow-x-auto p-[1px]'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead></TableHead>
                    <TableHead className='w-[35%]'>Name</TableHead>
                    <TableHead className='w-[55%]'>Description</TableHead>
                    <TableHead className='w-[10%]'>Created at</TableHead>
                    <TableHead className='w-[10%]'>Visibility</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  <If condition={series.length < 1}>
                    <TableRow>
                      <TableCell colSpan='5'>You don't have any pathways yet, go ahead and create one 😀</TableCell>
                    </TableRow>
                  </If>

                  <For each='s' of={series}>
                    <TableRow
                      key={s.id}
                      className='cursor-pointer hover:bg-muted'
                      onClick={() => navigate(`/educators/courses/${s.id}`)}
                    >
                      <TableCell>
                        <div className='m-1'>
                          <Choose>
                            <When condition={s.coverImage?.url}>
                              <img src={s.coverImage.url} className='size-10 rounded-md' />
                            </When>

                            <Otherwise>
                              {(() => {
                                const { emoji, gradientFrom, gradientTo } = getCourseVisualStyle(s.id);
                                return (
                                  <div
                                    className='inline-flex shrink-0 size-10 items-center justify-center rounded-md'
                                    style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
                                  >
                                    <span className='text-lg'>{emoji}</span>
                                  </div>
                                );
                              })()}
                            </Otherwise>
                          </Choose>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-row items-center gap-2">

                          <span className='block'>
                            <Choose>
                              <When condition={s.name}>
                                {s.name}
                              </When>
                              <Otherwise>
                                Untitled pathway
                              </Otherwise>
                            </Choose>
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {truncate(s.description, { length: 70 })}
                      </TableCell>
                      <TableCell>{formatShortTimestamp(s.createdAt)}</TableCell>
                      <TableCell>
                        <VisiblilityBadge visibility={s.visibility} />
                      </TableCell>
                    </TableRow>
                  </For>
                </TableBody>
              </Table>
            </div>

            <Pagination page={page} pagesCount={pagesCount} setPage={setPage} />
          </div>
        </Otherwise>
      </Choose>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='sm:max-w-[425px]'>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className='flex flex-col'
          >
            <DialogHeader>
              <DialogTitle>New pathway</DialogTitle>
              <DialogDescription>
                Create a new pathway to organize your projects.
              </DialogDescription>
            </DialogHeader>
            <div className='flex flex-col gap-4 py-4'>
              <div className='space-y-2'>
                <Label htmlFor='name'>
                  Name
                </Label>
                <Input
                  id='name'
                  required
                  {...register('name', { required: 'Name is required' })}
                />
                <If condition={errors.name}>
                  <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>{errors.name.message}</p>
                </If>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='description'>
                  Description
                </Label>
                <Textarea
                  id='description'
                  required={false}
                  {...register('description')}
                />
                <If condition={errors.description}>
                  <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>{errors.description.message}</p>
                </If>
              </div>

              <div className='space-y-2'>
                <Label>Visibility</Label>
                <Controller
                  name='visibility'
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                      disabled
                    >
                      <SelectTrigger className='w-full !h-auto py-2'>
                        <div className='flex items-start'>
                          <LockClosedIcon className='h-4 w-4 mr-2 flex-shrink-0' />
                          <div className="flex flex-col items-start text-left">
                            <span>Private</span>
                            <span className='text-xs text-muted-foreground'>This pathway will only be visible to you.</span>
                          </div>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='PRIVATE'>
                          <div className='flex items-center'>
                            <LockClosedIcon className='h-4 w-4 mr-2' />
                            <div className="flex flex-col text-left">
                              <span>Private</span>
                              <p className='text-sm text-muted-foreground pt-1'>This pathway will only be visible to you.</p>
                            </div>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                disabled={isCreating}
                type='submit'
              >
                <If condition={isCreating}>
                  Creating...
                  <Else />
                  Create pathway
                </If>
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default SeriesList
